import React, { useCallback, useEffect, useState } from 'react'
import {
  ShieldCheckIcon,
  LockClosedIcon,
  LockOpenIcon,
  PencilSquareIcon,
  ClockIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import { CDD_TIERS, cddTierMeta } from '../../../components/patron/cddTierMeta'
import CddTierBadge from '../../../components/patron/CddTierBadge'

/**
 * CDD Tier Card — S2 of the production-readiness tracker.
 *
 * Renders the patron's current CDD intensity tier, who/what set it, and
 * whether a manual override is in force. Lets a CO override or revert,
 * and surfaces the transition history.
 *
 * Drops into any page that has a member_id in scope (ProfileDetail,
 * InvestigationCase, watchlist member overview).
 */

const TIERS = CDD_TIERS

const SOURCE_LABEL = { auto: 'Auto-classified from risk', manual: 'Manual override by CO', system: 'Seeded by system' }

function tierMeta(tier) {
  return cddTierMeta(tier)
}

function formatDateTime(v) {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
}

function TierBadge({ tier, size = 'md' }) {
  return <CddTierBadge tier={tier} size={size === 'lg' ? 'default' : 'compact'} />
}

function OverrideModal({ open, onClose, current, onSubmit, submitting }) {
  const [tier, setTier] = useState(current || 'standard')
  const [reason, setReason] = useState('')
  useEffect(() => {
    if (open) { setTier(current || 'standard'); setReason('') }
  }, [open, current])
  if (!open) return null
  const ok = reason.trim().length >= 10 && TIERS.find(t => t.value === tier)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg border border-gray-100">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-psg-teal">Override CDD tier</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-600 leading-relaxed">
            A manual override locks the tier against the automatic risk-score resolver.
            The patron will stay in the chosen tier until you click <b>Clear override</b>.
            The reason is permanent audit and visible to assessors.
          </p>
          <div>
            <label htmlFor="ct-tier" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              New tier <span className="text-red-500">*</span>
            </label>
            <select
              id="ct-tier"
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              {TIERS.map((t) => (
                <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="ct-reason" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Reason <span className="text-red-500">*</span>
              </label>
              <span className={`text-xs font-mono ${reason.trim().length >= 10 ? 'text-green-600' : 'text-gray-400'}`}>
                {reason.trim().length} / 10 min
              </span>
            </div>
            <textarea
              id="ct-reason"
              rows={4}
              maxLength={4000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Patron identified as PEP during enhanced review; moving to Enhanced pending senior management sign-off."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 resize-y outline-none focus:border-gray-300"
              autoFocus
            />
          </div>
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >Cancel</button>
          <button
            type="button"
            onClick={() => onSubmit({ tier, reason: reason.trim() })}
            disabled={!ok || submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-psg-teal text-white hover:bg-psg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <LockClosedIcon className="w-4 h-4" />
            {submitting ? 'Saving…' : 'Override & lock'}
          </button>
        </div>
      </div>
    </div>
  )
}

function HistoryDrawer({ open, onClose, history, loading }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} role="presentation" />
      <div className="relative bg-white w-full max-w-md h-full shadow-xl overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-4 border-b bg-white z-10">
          <h3 className="text-lg font-semibold text-psg-teal">CDD tier history</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          {loading ? (
            <p className="text-sm text-gray-500 py-6 text-center">Loading history…</p>
          ) : !history || history.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No transitions recorded.</p>
          ) : (
            <ul className="space-y-3">
              {history.map((h) => (
                <li key={h.id} className="border-l-2 border-psg-teal/30 pl-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {h.from_tier ? <TierBadge tier={h.from_tier} /> : <span className="text-xs text-gray-500">(new)</span>}
                    <span className="text-gray-400 text-xs">→</span>
                    <TierBadge tier={h.to_tier} />
                    <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${
                      h.source === 'manual' ? 'bg-amber-100 text-amber-800' :
                      h.source === 'auto'   ? 'bg-slate-100 text-slate-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{h.source}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 font-mono">
                    {formatDateTime(h.set_at)}
                    {h.set_by_name || h.set_by ? <> · {h.set_by_name || h.set_by}</> : null}
                  </div>
                  {h.reason && (
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{h.reason}</p>
                  )}
                  {h.risk_snapshot && typeof h.risk_snapshot === 'object' && (
                    <div className="mt-1 text-[11px] text-gray-500 font-mono">
                      {h.risk_snapshot.risk_score != null && <>score {h.risk_snapshot.risk_score} · </>}
                      {h.risk_snapshot.pep_match === true && <>PEP match · </>}
                      {h.risk_snapshot.sanctions_match === true && <>sanctions match · </>}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default function CddTierCard({ memberId, compact = false, dense = false, refreshKey = 0, onTierLoaded }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [history, setHistory] = useState(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [overrideOpen, setOverrideOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState(null)

  const load = useCallback(async () => {
    if (!memberId) return
    setLoading(true); setError(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cdd-tier/${encodeURIComponent(memberId)}`))
      const json = await res.json().catch(() => ({}))
      if (res.status === 404) { setData(null); onTierLoaded?.(null); return }
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setData(json.data)
      onTierLoaded?.(json.data?.cdd_tier || null)
    } catch (e) {
      setError(e.message || 'Failed to load CDD tier')
      onTierLoaded?.(null)
    } finally {
      setLoading(false)
    }
  }, [memberId, onTierLoaded])

  const loadHistory = useCallback(async () => {
    if (!memberId) return
    setHistoryLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cdd-tier/${encodeURIComponent(memberId)}/history`))
      const json = await res.json().catch(() => ({}))
      setHistory(json.success ? json.data || [] : [])
    } catch (_) { setHistory([]) }
    finally { setHistoryLoading(false) }
  }, [memberId])

  useEffect(() => { load() }, [load, refreshKey])

  const handleOverride = useCallback(async ({ tier, reason }) => {
    if (saving) return
    setSaving(true); setActionError(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/cdd-tier/${encodeURIComponent(memberId)}/override`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tier, reason }),
        }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setOverrideOpen(false)
      await load()
    } catch (e) { setActionError(e.message || 'Override failed') }
    finally { setSaving(false) }
  }, [saving, memberId, load])

  const handleClear = useCallback(async () => {
    if (saving) return
    const reason = prompt('Reason for clearing the override (optional):') || ''
    setSaving(true); setActionError(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/cdd-tier/${encodeURIComponent(memberId)}/clear`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      await load()
    } catch (e) { setActionError(e.message || 'Clear failed') }
    finally { setSaving(false) }
  }, [saving, memberId, load])

  if (!memberId) return null

  if (compact) {
    return (
      <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
        <span className="text-[11px] text-slate-400 flex-shrink-0 mr-2">CDD Tier</span>
        <span className="text-[11px] text-slate-700 text-right inline-flex items-center gap-1.5 justify-end">
          {loading && '…'}
          {!loading && error && '—'}
          {!loading && !error && !data && '—'}
          {!loading && !error && data && (
            <>
              <TierBadge tier={data.cdd_tier} />
              {data.cdd_tier_locked && (
                <LockClosedIcon className="w-3.5 h-3.5 text-amber-700" title="Manual override locked" />
              )}
            </>
          )}
        </span>
      </div>
    )
  }

  const actionBtnClass =
    'px-2 py-1 rounded border border-gray-200 text-[11px] font-semibold text-gray-600 bg-white hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50'

  if (dense) {
    const desc = tierMeta(data?.cdd_tier)?.desc
    const metaLine = data
      ? [SOURCE_LABEL[data.cdd_tier_source] || null, desc].filter(Boolean).join(' · ')
      : null

    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 min-h-[1.75rem]">
          <div className="flex items-center gap-1.5 shrink-0">
            <ShieldCheckIcon className="w-4 h-4 text-psg-teal" aria-hidden />
            <span className="text-xs font-semibold text-gray-800">CDD Tier</span>
          </div>

          {loading && <span className="text-xs text-gray-500">Loading…</span>}
          {error && (
            <span className="text-xs text-red-600 inline-flex items-center gap-1">
              <ExclamationTriangleIcon className="w-3.5 h-3.5 shrink-0" />
              {error}
            </span>
          )}
          {!loading && !error && !data && (
            <span className="text-xs text-gray-500">Tier set on first risk evaluation.</span>
          )}

          {!loading && !error && data && (
            <>
              <div className="flex items-center gap-1.5 shrink-0">
                <TierBadge tier={data.cdd_tier} />
                {data.cdd_tier_locked && (
                  <span
                    title="Manual override locked"
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                  >
                    <LockClosedIcon className="w-2.5 h-2.5" /> Locked
                  </span>
                )}
                {data.is_pep && (
                  <span className="px-1 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-medium">PEP</span>
                )}
              </div>
              {metaLine && (
                <p className="text-[11px] text-gray-500 truncate min-w-0 flex-1" title={metaLine}>
                  {metaLine}
                </p>
              )}
            </>
          )}

          <div className="flex items-center gap-1.5 ml-auto shrink-0">
            <button
              type="button"
              onClick={() => { setHistoryOpen(true); loadHistory() }}
              className="text-[11px] font-semibold text-psg-teal hover:underline inline-flex items-center gap-0.5"
            >
              <ClockIcon className="w-3.5 h-3.5" /> History
            </button>
            {!loading && !error && data && (
              <>
                <button
                  type="button"
                  onClick={() => setOverrideOpen(true)}
                  disabled={saving}
                  className={actionBtnClass}
                >
                  <PencilSquareIcon className="w-3 h-3" />
                  {data.cdd_tier_locked ? 'Change' : 'Override'}
                </button>
                {data.cdd_tier_locked && (
                  <button type="button" onClick={handleClear} disabled={saving} className={actionBtnClass}>
                    <LockOpenIcon className="w-3 h-3" /> Clear
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {actionError && (
          <div className="mt-1.5 text-[11px] text-red-600 flex items-start gap-1">
            <ExclamationTriangleIcon className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span>{actionError}</span>
          </div>
        )}

        <OverrideModal
          open={overrideOpen}
          onClose={() => !saving && setOverrideOpen(false)}
          current={data?.cdd_tier}
          onSubmit={handleOverride}
          submitting={saving}
        />
        <HistoryDrawer
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          history={history}
          loading={historyLoading}
        />
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="w-5 h-5 text-psg-teal" />
          <h3 className="text-base font-semibold text-gray-900 m-0">CDD Tier</h3>
        </div>
        <button
          type="button"
          onClick={() => { setHistoryOpen(true); loadHistory() }}
          className="text-xs text-psg-teal hover:underline inline-flex items-center gap-1"
        >
          <ClockIcon className="w-4 h-4" /> History
        </button>
      </div>

      {loading && <p className="text-sm text-gray-500 py-2">Loading…</p>}
      {error && (
        <div className="p-2.5 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && !data && (
        <p className="text-sm text-gray-500">No patron profile yet — tier will be set on first risk evaluation.</p>
      )}

      {!loading && !error && data && (
        <>
          <div className="flex items-start gap-4">
            {/* Left: badge + source */}
            <div className="flex-shrink-0">
              <div className="flex items-center gap-2 flex-wrap">
                <TierBadge tier={data.cdd_tier} size="lg" />
                {data.cdd_tier_locked && (
                  <span
                    title="Manual override locked — auto-resolver will not change the tier"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                  >
                    <LockClosedIcon className="w-3 h-3" /> Locked
                  </span>
                )}
                {data.is_pep && <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-medium">PEP</span>}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {SOURCE_LABEL[data.cdd_tier_source] || '—'}
              </div>
            </div>
            {/* Right: description */}
            {tierMeta(data.cdd_tier) && (
              <p className="text-xs text-gray-500 leading-relaxed flex-1 pt-0.5">{tierMeta(data.cdd_tier).desc}</p>
            )}
          </div>

          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => setOverrideOpen(true)}
              disabled={saving}
              className="px-3 py-1.5 rounded-md border border-gray-200 text-sm text-gray-700 bg-white hover:bg-gray-50 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <PencilSquareIcon className="w-4 h-4" />
              {data.cdd_tier_locked ? 'Change override' : 'Override'}
            </button>
            {data.cdd_tier_locked && (
              <button
                type="button"
                onClick={handleClear}
                disabled={saving}
                className="px-3 py-1.5 rounded-md border border-gray-200 text-sm text-gray-700 bg-white hover:bg-gray-50 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <LockOpenIcon className="w-4 h-4" />
                Clear override
              </button>
            )}
          </div>

          {actionError && (
            <div className="mt-3 p-2.5 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{actionError}</span>
            </div>
          )}
        </>
      )}

      <OverrideModal
        open={overrideOpen}
        onClose={() => !saving && setOverrideOpen(false)}
        current={data?.cdd_tier}
        onSubmit={handleOverride}
        submitting={saving}
      />
      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={history}
        loading={historyLoading}
      />
    </div>
  )
}
